import React, { useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatContext } from '../../context/ChatContext';
import MessageBubble from './MessageBubble';
import ChatHeader from './ChatHeader';
import { useToast } from '../commons/toast/ToastContext';

const ChatRoom = ({ userId, isTrainer, schoolId, onBack }) => {
    const { t, i18n } = useTranslation('chat');
    const {
        activeChatRoom,
        messages,
        messagesLoading,
        sendMessage,
        markAsRead,
        fetchMessages,
        sendTypingEvent,
        typingUsers,
        // TODO: Re-enable when emoji reactions are redesigned
        // addReaction,
        // removeReaction,
        pinMessage,
        unpinMessage,
        fetchPinnedMessages,
        onlineUsers
    } = useContext(ChatContext);
    const toast = useToast();

    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [replyingTo, setReplyingTo] = useState(null);
    const [showScrollBottom, setShowScrollBottom] = useState(false);
    const [newMessageCount, setNewMessageCount] = useState(0);
    const [pinnedMessages, setPinnedMessages] = useState([]);
    const [showPinnedPanel, setShowPinnedPanel] = useState(false);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const isNearBottomRef = useRef(true);
    const prevMessagesLengthRef = useRef(0);

    const isWritable = activeChatRoom?.status === 'ACTIVE';
    const currentParticipant = activeChatRoom?.participants?.find(p => p.userId === userId);
    const canWrite = isWritable && currentParticipant &&
        currentParticipant.role !== 'READ_ONLY';

    const isDirectChat = activeChatRoom?.type === 'DIRECT';

    // Find the other participant for DIRECT chats
    const otherParticipant = isDirectChat
        ? activeChatRoom?.participants?.find(p => p.userId !== userId)
        : null;

    // Fetch pinned messages on room open
    useEffect(() => {
        if (activeChatRoom?.id) {
            fetchPinnedMessages(activeChatRoom.id).then(data => {
                setPinnedMessages(data || []);
            });
        }
    }, [activeChatRoom?.id, fetchPinnedMessages]);

    // Scroll to bottom on new messages (only if near bottom)
    useEffect(() => {
        if (messages.length > prevMessagesLengthRef.current && !isNearBottomRef.current && page === 0) {
            // New message arrived while scrolled up
            setNewMessageCount(prev => prev + (messages.length - prevMessagesLengthRef.current));
        } else if (messagesEndRef.current && page === 0 && isNearBottomRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        prevMessagesLengthRef.current = messages.length;
    }, [messages, page]);

    // Mark as read when viewing messages
    useEffect(() => {
        if (activeChatRoom?.id) {
            markAsRead(activeChatRoom.id);
        }
    }, [activeChatRoom?.id, messages.length, markAsRead]);

    // Auto-resize textarea when content changes
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = Math.min(textarea.scrollHeight, 160) + 'px';
        }
    }, [newMessage]);

    const handleScroll = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        const { scrollTop, scrollHeight, clientHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        isNearBottomRef.current = distanceFromBottom < 200;
        setShowScrollBottom(distanceFromBottom > 200);
        if (distanceFromBottom < 200) {
            setNewMessageCount(0);
        }
    }, []);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        setNewMessageCount(0);
        setShowScrollBottom(false);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || sending || !canWrite) return;

        try {
            setSending(true);
            await sendMessage(activeChatRoom.id, newMessage.trim(), replyingTo?.id || null);
            setNewMessage('');
            setReplyingTo(null);
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
        } catch (err) {
            toast.error(err.message || t('actions.sendError'));
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    const handleInputChange = (e) => {
        setNewMessage(e.target.value);
        if (activeChatRoom?.id) {
            sendTypingEvent(activeChatRoom.id);
        }
    };

    const handleLoadMore = async () => {
        const nextPage = page + 1;
        const result = await fetchMessages(activeChatRoom.id, nextPage);
        if (result) {
            setPage(nextPage);
            if (result.content.length === 0 || result.last) {
                setHasMore(false);
            }
        }
    };

    const handleReply = (message) => {
        setReplyingTo(message);
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    const handleUnpinFromPanel = async (messageId) => {
        try {
            await unpinMessage(messageId);
            setPinnedMessages(prev => prev.filter(m => m.id !== messageId));
        } catch (err) {
            toast.error(err.message);
        }
    };

    // Determine read receipt for DIRECT chats
    const getShowReadReceipt = (msg, index) => {
        if (!isDirectChat || !otherParticipant?.lastReadAt) return false;
        if (msg.senderId !== userId) return false;

        const otherLastRead = new Date(otherParticipant.lastReadAt);
        const msgCreated = new Date(msg.createdAt);
        if (msgCreated > otherLastRead) return false;

        // Only show on the last consecutive own message that was read
        const nextMsg = messages[index + 1];
        if (nextMsg && nextMsg.senderId === userId) {
            const nextMsgCreated = new Date(nextMsg.createdAt);
            if (nextMsgCreated <= otherLastRead) return false;
        }
        return true;
    };

    // Typing indicator
    const typingData = typingUsers[activeChatRoom?.id];
    const showTyping = isDirectChat && typingData;

    const pinnedCount = pinnedMessages.length;

    if (!activeChatRoom) return null;

    return (
        <div className="chat-room">
            <ChatHeader
                chatRoom={activeChatRoom}
                userId={userId}
                isTrainer={isTrainer}
                schoolId={schoolId}
                onBack={onBack}
            />

            {/* Pinned messages bar */}
            {pinnedCount > 0 && (
                <>
                    <div
                        className="chat-pinned-bar"
                        onClick={() => setShowPinnedPanel(!showPinnedPanel)}
                    >
                        <span>📌</span>
                        <span>
                            {t('pinnedMessage', { count: pinnedCount })}
                        </span>
                    </div>
                    {showPinnedPanel && (
                        <div className="chat-pinned-panel">
                            {pinnedMessages.map(pm => (
                                <div key={pm.id} className="chat-pinned-message">
                                    <div className="chat-pinned-message-sender">
                                        {pm.senderFirstName} {pm.senderLastName}
                                    </div>
                                    <div className="chat-pinned-message-content">
                                        {pm.content}
                                    </div>
                                    <div className="chat-pinned-message-actions">
                                        <button onClick={() => handleUnpinFromPanel(pm.id)}>
                                            {t('unpin')}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            <div
                className="chat-messages"
                ref={messagesContainerRef}
                onScroll={handleScroll}
            >
                {hasMore && messages.length >= 50 && (
                    <div className="chat-load-more">
                        <button
                            onClick={handleLoadMore}
                            disabled={messagesLoading}
                            className="chat-load-more-btn"
                        >
                            {messagesLoading ? t('loading') : t('loadOlder')}
                        </button>
                    </div>
                )}

                {messagesLoading && messages.length === 0 ? (
                    <div className="chat-loading">{t('loadingMessages')}</div>
                ) : messages.length === 0 ? (
                    <div className="chat-no-messages">
                        <p>{t('noMessages')}</p>
                        <p>{t('noMessagesStart')}</p>
                    </div>
                ) : (
                    messages.map((msg, index) => {
                        const prevMsg = index > 0 ? messages[index - 1] : null;
                        const showSender = !prevMsg || prevMsg.senderId !== msg.senderId;
                        const showDate = !prevMsg || !isSameDay(prevMsg.createdAt, msg.createdAt);

                        return (
                            <React.Fragment key={msg.id}>
                                {showDate && (
                                    <div className="chat-date-separator">
                                        <span>{formatDateSeparator(msg.createdAt, t, i18n.language)}</span>
                                    </div>
                                )}
                                <MessageBubble
                                    message={msg}
                                    isOwn={msg.senderId === userId}
                                    showSender={showSender}
                                    userId={userId}
                                    onReply={handleReply}
                                    showReadReceipt={getShowReadReceipt(msg, index)}
                                />
                            </React.Fragment>
                        );
                    })
                )}
                <div ref={messagesEndRef} />

                {/* Scroll to bottom button */}
                {showScrollBottom && (
                    <button className="chat-scroll-bottom" onClick={scrollToBottom}>
                        ↓
                        {newMessageCount > 0 && (
                            <span className="chat-scroll-bottom-badge">{newMessageCount}</span>
                        )}
                    </button>
                )}
            </div>

            {/* Typing indicator */}
            {showTyping && (
                <div className="chat-typing-indicator">
                    {typingData.firstName} {t('typing')}
                </div>
            )}

            {!isWritable && (
                <div className="chat-status-bar">
                    {activeChatRoom.status === 'FINISHED'
                        ? t('chatFinished')
                        : t('chatArchived')}
                </div>
            )}

            {isWritable && !canWrite && currentParticipant?.role === 'READ_ONLY' && (
                <div className="chat-status-bar">
                    {t('readOnlyTrainersOnly')}
                </div>
            )}

            {/* Reply bar */}
            {canWrite && replyingTo && (
                <div className="chat-reply-bar">
                    <div className="chat-reply-bar-content">
                        <div className="chat-reply-bar-sender">
                            {replyingTo.senderFirstName} {replyingTo.senderLastName}
                        </div>
                        <div className="chat-reply-bar-text">
                            {replyingTo.content?.length > 80
                                ? replyingTo.content.substring(0, 80) + '...'
                                : replyingTo.content}
                        </div>
                    </div>
                    <button
                        className="chat-reply-bar-close"
                        onClick={() => setReplyingTo(null)}
                    >
                        ✕
                    </button>
                </div>
            )}

            {canWrite && (
                <form className="chat-input-area" onSubmit={handleSendMessage}>
                    <textarea
                        ref={textareaRef}
                        value={newMessage}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={t('typeMessage')}
                        className="chat-input"
                        rows={1}
                        maxLength={5000}
                        disabled={sending}
                    />
                    <button
                        type="submit"
                        className="chat-send-btn"
                        disabled={!newMessage.trim() || sending}
                    >
                        {sending ? '...' : '➤'}
                    </button>
                </form>
            )}
        </div>
    );
};

function isSameDay(date1, date2) {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear()
        && d1.getMonth() === d2.getMonth()
        && d1.getDate() === d2.getDate();
}

function formatDateSeparator(dateStr, t, language) {
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('today');
    if (diffDays === 1) return t('yesterday');
    const locale = language === 'pl' ? 'pl-PL' : 'en-US';
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}

export default ChatRoom;
